import { addDays, differenceInCalendarDays, format, isSameDay } from 'date-fns'
import { useParams } from 'react-router-dom'

import {
  ApiAccountSnapshot,
  useGetByTenantAccountAndAccountSourceAccountIdentifierQuery
} from '@/api/rtk-api'
import { TENANT } from '@/const'
import { Currency } from '@/utilities/currency'

import { Row } from '../../pages/trade-events'
import { FeeOperation } from '../fee-table'
import { DepositWithdrawal } from './events/deposit-withdrawal'
import { EventHeader } from './events/event-header'
import { EventList } from './events/event-list'
import { EventNav } from './events/event-nav'
import { EventTables } from './events/event-tables'
import { ExternalTransfer } from './events/external-transfer'
import { GapPayment } from './events/gap-payment'
import { OrderCorrection } from './events/order-correction'
import { SwitchIn } from './events/switch-in'
import { SwitchOut } from './events/switch-out'
import classes from './index.module.scss'
import { Loading } from './Loading'

export const TradeEventsEvents = ({ rows }: { rows: Row[] }) => {
  const { date: stringDate, operationId, source, tradeId } = useParams()
  const selectedDate = new Date(stringDate)
  const { data, isLoading } = useGetByTenantAccountAndAccountSourceAccountIdentifierQuery({
    tenant: TENANT,
    accountIdentifier: tradeId,
    accountSource: source
  })

  if (isLoading) return <Loading />

  const { accountSnapShots } = data

  const snapshot: ApiAccountSnapshot = accountSnapShots.find(snapshot =>
    isSameDay(new Date(snapshot.valuationDate), new Date(stringDate))
  )
  const selectedOp = snapshot.operations.find(op => op.operationId === operationId)
  const feeOps = snapshot.operations.filter(op =>
    ['TriggeredFeeDeduction', 'FeeDeduction'].includes(op.type)
  )
  const selectedRow = rows.find(row => row.id === operationId)

  if (!selectedRow) return <Loading />

  const selectedOpDates = new Array(
    differenceInCalendarDays(selectedRow.end, selectedRow.start) + 1
  )
    .fill(null)
    .map((x, index) => addDays(new Date(selectedRow.start), index))

  const isDepositOrWithdrawal = ['Deposit', 'Withdrawal'].includes(selectedRow.readableType)
  const isExternalTransfer = selectedRow.type === 'ExternalTransfer'
  const isGapPayment = selectedRow.type === 'GapPayment'
  const isOrderCorrection = selectedRow.type === 'OrderCorrection'
  const isSwitchOut = ['ProtectedSwitchOut', 'SwitchOut'].includes(selectedRow.type)
  const isSwitchIn = selectedRow.type === 'SwitchIn'

  const formattedStartDate = format(selectedRow.start, 'dd/MM/yyyy')

  return (
    <div className={classes.root}>
      <EventList
        operationId={operationId}
        rows={rows}
        selectedDate={selectedDate}
        source={source}
        tradeId={tradeId}
      />
      <div className={classes.details}>
        <EventHeader row={selectedRow} />

        <EventNav
          dates={selectedOpDates}
          row={selectedRow}
          selectedDate={selectedDate}
          source={source}
          tradeId={tradeId}
        />

        {isDepositOrWithdrawal && (
          <DepositWithdrawal
            currency={snapshot.notionalCurrency as Currency}
            date={formattedStartDate}
            operation={selectedOp}
          />
        )}

        {isExternalTransfer && (
          <ExternalTransfer date={formattedStartDate} operation={selectedOp} />
        )}

        {isGapPayment && (
          <GapPayment
            currency={snapshot.notionalCurrency as Currency}
            date={formattedStartDate}
            operation={selectedOp}
          />
        )}

        {isOrderCorrection && (
          <OrderCorrection
            currency={snapshot.notionalCurrency as Currency}
            operation={selectedOp}
          />
        )}

        {isSwitchOut && (
          <SwitchOut
            currency={snapshot.notionalCurrency as Currency}
            operation={selectedOp as any}
            value={snapshot.accountValue}
          />
        )}

        {isSwitchIn && (
          <SwitchIn
            currency={snapshot.notionalCurrency as Currency}
            date={selectedDate}
            operation={selectedOp as any}
            source={source}
          />
        )}

        <EventTables
          currency={snapshot.notionalCurrency as Currency}
          feeOperations={feeOps as FeeOperation[]}
          operation={selectedOp}
          row={selectedRow}
          snapshot={snapshot}
        />
      </div>
    </div>
  )
}
